import React, { ComponentType, ElementType } from 'react';
import classNames from 'classnames';
import styles from './RelatedArticleTile.module.scss';
import Link from 'next/link';
import useWindowDimensions from 'hooks/useWindowDimensions';
import { ImageField, NextImage } from '@sitecore-jss/sitecore-jss-nextjs';
import Readtime from 'components/includes/Readtime';
import useHelpers from 'hooks/helpers';
import { useI18n } from 'next-localization';
import { ArticleCardData } from 'model/Article';
import { useCalculatedArticleFields } from 'hooks/useCalculatedArticleField';

export type GetBadgeValue = (article: ArticleCardData) => string | undefined;

interface RelatedArticleTileProps {
  articleCard: ArticleCardData;
  baseUrl?: string;
  getBadgeValue?: GetBadgeValue;
}

const RelatedArticleTile: React.FC<RelatedArticleTileProps> = ({
  articleCard,
  baseUrl,
  getBadgeValue = (article) => article.articleCategory.targetItem.name.value,
}) => {
  const { t } = useI18n();
  const isTechnicalArticle = articleCard.articleCategory?.targetItem?.name.value === 'Technical';
  const { getUrl, getArticleType } = useCalculatedArticleFields();

  const articleUrl = getUrl(articleCard.name, baseUrl);
  const attributes = isTechnicalArticle
    ? { href: articleUrl, target: '_blank', rel: 'noopener noreferrer' }
    : { href: articleUrl };

  const opensInNewWindow = !!attributes.target;

  const deviceType: string = useWindowDimensions();
  const Component: ComponentType | ElementType = isTechnicalArticle ? 'a' : Link;
  const { localizeDate } = useHelpers();
  const badgeValue = getBadgeValue(articleCard);

  return (
    <React.Fragment>
      <Component {...attributes} className={styles.relatedArticle}>
        {articleCard.articleImage?.src && deviceType === 'mobile' && (
          <div
            className={classNames(
              styles.relatedArticleThumbnail,
              'flex-shrink-0 position-relative'
            )}
          >
            <NextImage
              fill
              field={articleCard.articleImage as ImageField}
              sizes="(max-width: 575px) 100vw, 495px"
            />
          </div>
        )}

        <div
          className={classNames(
            articleCard.articleImage?.src
              ? styles.relatedArticleMain
              : styles.relatedArticleMainWithoutThumbnail,
            'd-flex',
            'flex-column',
            'flex-grow-1',
            'background-white',
            'p-3',
            'pb-4'
          )}
        >
          <div
            className={classNames(
              styles.relatedArticleHeading,
              'd-flex',
              'flex-shrink-0',
              'align-items-center',
              'text-grey'
            )}
          >
            {badgeValue && (
              <span className={classNames(styles.articleCategory, 'section-header-small')}>
                {badgeValue}
              </span>
            )}

            <Readtime
              articleType={getArticleType(articleCard.template)}
              duration={articleCard.duration?.value ?? 0}
              className={classNames('section-header-small', 'ms-2')}
            />
          </div>

          <div
            className={classNames(
              styles.relatedArticleBody,
              'd-flex',
              'flex-grow-1',
              'align-items-start',
              'mt-2'
            )}
          >
            <div
              className={classNames(
                styles.relatedArticleContent,
                'd-flex',
                'flex-column',
                'h-100',
                'flex-grow-1'
              )}
            >
              <h3 className="h5">{articleCard.articleTitle.value}</h3>
              <p
                className={classNames('body-small', 'text-grey', 'mt-2', 'mb-4', styles.lineClamp3)}
              >
                {articleCard.articleStrapline.value}
              </p>
              <time className="d-block caption text-grey mt-auto text-uppercase">
                {localizeDate(articleCard.articlePublicationDate.formattedDateValue)}
              </time>
            </div>

            {articleCard.articleImage?.src && deviceType !== 'mobile' && (
              <div
                className={classNames(
                  styles.relatedArticleThumbnail,
                  'flex-shrink-0 ms-3 position-relative'
                )}
              >
                <NextImage field={articleCard.articleImage as ImageField} sizes="170px" fill />
              </div>
            )}
          </div>
        </div>

        {opensInNewWindow && <span className="sr-only">{t('opensInNewWindow')}</span>}
      </Component>
    </React.Fragment>
  );
};

export default RelatedArticleTile;
